import { useEffect, useState, useCallback } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Copy } from "lucide-react";

const fmt = (n) => Number(n).toLocaleString("en-US");

function fmtDateCell(r) {
  if (r.date_str) return r.date_str;
  if (r.appeared_at) return new Date(r.appeared_at).toLocaleString("sv-SE").replace("T", " ");
  return "";
}

/* ---------------- Pending table (Deposit / Withdraw) ---------------- */
function PendingTable({ type }) {
  const [rows, setRows] = useState([]);
  const [running, setRunning] = useState(false);
  const [selected, setSelected] = useState({});
  const [bankFilter, setBankFilter] = useState({});
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    api.get(`/transactions/pending?type=${type}`).then((r) => {
      setRows(r.data.transactions);
      setRunning(r.data.session_running);
    }).catch(() => {});
  }, [type]);

  useEffect(() => {
    load();
    const t = setInterval(load, 4000);
    return () => clearInterval(t);
  }, [load]);

  const isDeposit = type === "Deposit";
  const colCount = isDeposit ? 9 : 8;

  // Filter chips = bank/e-wallet TUJUAN (destination), dihitung dari transaksi pending
  const bankOf = (r) => (r.destination_bank || (r.bank || "").split(",")[0] || "?").trim() || "?";
  const bankCounts = {};
  rows.forEach((r) => { const b = bankOf(r); bankCounts[b] = (bankCounts[b] || 0) + 1; });
  const activeBanks = Object.keys(bankFilter).filter((k) => bankFilter[k]);
  const shown = activeBanks.length ? rows.filter((r) => activeBanks.includes(bankOf(r))) : rows;

  const toggle = (id) => setSelected((s) => ({ ...s, [id]: !s[id] }));
  const uncheckAll = () => {
    const anySelected = Object.values(selected).some(Boolean);
    if (anySelected) return setSelected({});
    const all = {};
    shown.forEach((r) => (all[r.id] = true));
    setSelected(all);
  };
  const openTxn = (id) => api.post(`/transactions/${id}/open`).catch(() => {});
  const copy = (v) => { navigator.clipboard?.writeText(String(v)); toast.success("Disalin"); };

  const act = async (action) => {
    const ids = Object.keys(selected).filter((k) => selected[k]);
    if (ids.length === 0) return toast.error("Pilih minimal satu transaksi");
    setBusy(true);
    try {
      const { data } = await api.post("/transactions/action", { transaction_ids: ids, action });
      toast.success(`${data.processed} transaksi di-${action === "ACCEPT" ? "Accept" : "Reject"}`);
      setSelected({});
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setBusy(false); }
  };

  // Tekan Enter untuk Accept transaksi yang dicentang (tanpa mouse)
  useEffect(() => {
    const onKey = (e) => {
      if (e.key !== "Enter") return;
      const tag = (e.target.tagName || "").toLowerCase();
      if (tag === "textarea" || tag === "select") return;
      if (tag === "input" && e.target.type !== "checkbox") return;
      const hasSel = Object.values(selected).some(Boolean);
      if (hasSel && !busy) { e.preventDefault(); act("ACCEPT"); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [selected, busy]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div>
      <div className="table-toolbar">
        <button className="btn-uncheck" onClick={uncheckAll} data-testid="uncheck-all">Un/check All</button>
        {Object.entries(bankCounts).map(([b, c]) => (
          <label key={b} className="bank-filter" data-testid={`bank-filter-${b}`}>
            <input type="checkbox" checked={!!bankFilter[b]} onChange={() => setBankFilter((s) => ({ ...s, [b]: !s[b] }))} />
            {b} [ {c} ]
          </label>
        ))}
      </div>

      {!running && <div className="muted" style={{ marginBottom: 8 }} data-testid="no-session-msg">Tidak ada training session yang berjalan.</div>}

      <table className="panel-table" data-testid={`pending-table-${type.toLowerCase()}`}>
        <thead>
          <tr>
            <th style={{ width: 34 }}>No</th>
            <th style={{ width: 24 }}></th>
            <th><span className="col-sort">User</span></th>
            <th><span className="col-sort">Date</span></th>
            <th className="text-center"><span className="col-sort">Amount</span></th>
            {isDeposit && <th className="text-center">Balance</th>}
            <th>{isDeposit ? "Bank Asal" : "Bank Tujuan"}</th>
            <th>Info</th>
            <th style={{ width: 60 }}>Tools</th>
          </tr>
        </thead>
        <tbody>
          {shown.length === 0 && <tr className="empty-row"><td colSpan={colCount}>Tidak ada transaksi pending</td></tr>}
          {shown.map((r, i) => (
            <tr key={r.id} data-testid={`txn-row-${r.transaction_id}`}>
              <td className="text-center">{i + 1}</td>
              <td className="text-center"><input type="checkbox" checked={!!selected[r.id]} onChange={() => toggle(r.id)} data-testid={`select-${r.transaction_id}`} /></td>
              <td>
                <div className="user-cell">{r.user}</div>
                <a className="link-blue" onClick={() => openTxn(r.id)} data-testid={`type-link-${r.transaction_id}`}>{r.display_type}</a>
              </td>
              <td className="nowrap">{fmtDateCell(r)}</td>
              <td className="text-right nowrap">
                <span className="amount-blue">{fmt(r.amount)}</span>
                <Copy size={11} className="copy-ico" onClick={() => copy(r.amount)} />
              </td>
              {isDeposit && (
                <td className="text-center">
                  <span className="balance-num">{r.balance}</span>
                  <a className="link-blue g-link">G{r.glevel || 1}</a>
                </td>
              )}
              <td>{r.bank}</td>
              <td className="info-cell">{r.info}</td>
              <td className="text-center"><button className="btn-gray" style={{ padding: "3px 10px" }} onClick={() => openTxn(r.id)} data-testid={`profil-${r.transaction_id}`}>Profil</button></td>
            </tr>
          ))}
        </tbody>
      </table>
      <div className="action-bar">
        <button className="btn btn-accept" disabled={busy} onClick={() => act("ACCEPT")} data-testid="accept-btn">Accept</button>
        <button className="btn btn-reject" disabled={busy} onClick={() => act("REJECT")} data-testid="reject-btn">Reject</button>
      </div>
    </div>
  );
}

/* ---------------- Manual ---------------- */
function ManualForm() {
  const [form, setForm] = useState({ user: "", transaction_type: "Deposit Manual", amount: "", description: "" });
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/transactions/manual", { ...form, amount: Number(form.amount) });
      toast.success("Transaksi manual tercatat di History Koin");
      setForm({ user: "", transaction_type: "Deposit Manual", amount: "", description: "" });
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <form onSubmit={submit} style={{ maxWidth: 520, paddingTop: 6 }} data-testid="manual-form">
      <div className="form-row"><label>User</label>
        <input className="form-input" value={form.user} onChange={(e) => setForm({ ...form, user: e.target.value })} data-testid="manual-user" required /></div>
      <div className="form-row"><label>Transaction Type</label>
        <select className="form-select" value={form.transaction_type} onChange={(e) => setForm({ ...form, transaction_type: e.target.value })} data-testid="manual-type">
          <option>Deposit Manual</option><option>Withdraw Manual</option><option>Adjustment</option>
        </select></div>
      <div className="form-row"><label>Amount</label>
        <input className="form-input" type="number" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} data-testid="manual-amount" required /></div>
      <div className="form-row"><label>Description</label>
        <textarea className="form-input" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="manual-desc" /></div>
      <div className="form-row"><label></label>
        <button className="btn btn-primary" disabled={busy} data-testid="manual-submit">Submit</button></div>
    </form>
  );
}

/* ---------------- Report (Laporan per operator/staff) ---------------- */
const famt = (n) => (Number(n) ? Number(n).toLocaleString("en-US") : "");

function ReportView() {
  const [data, setData] = useState(null);
  const [detail, setDetail] = useState(null);

  useEffect(() => { api.get("/report/laporan").then((r) => setData(r.data)).catch(() => {}); }, []);

  const openDetail = (sid, page = 1) =>
    api.get(`/report/laporan/${sid}?page=${page}&per_page=15`)
      .then((r) => setDetail({ ...r.data, staff_id: sid }))
      .catch(() => {});

  const exportCsv = (rows, headers, keys, name) => {
    const lines = [headers.join(",")];
    rows.forEach((r) => lines.push(keys.map((k) => `"${String(r[k] ?? "").replace(/"/g, '""')}"`).join(",")));
    const url = URL.createObjectURL(new Blob([lines.join("\n")], { type: "text/csv" }));
    const a = document.createElement("a"); a.href = url; a.download = name; a.click(); URL.revokeObjectURL(url);
  };

  // ---- Detail view ----
  if (detail) {
    const pages = [];
    for (let i = 1; i <= detail.total_pages; i++) pages.push(i);
    return (
      <div data-testid="laporan-detail">
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
          <button className="btn btn-gray" onClick={() => setDetail(null)} data-testid="laporan-back">&larr; Kembali</button>
          <div className="pagination">
            <span>{detail.page} of {detail.total_pages}</span>
            <a onClick={() => openDetail(detail.staff_id, 1)}>First</a>
            {pages.slice(0, 6).map((p) => (
              <a key={p} className={p === detail.page ? "active" : ""} onClick={() => openDetail(detail.staff_id, p)}>{p}</a>
            ))}
            <a onClick={() => openDetail(detail.staff_id, Math.min(detail.total_pages, detail.page + 1))}>Next</a>
            <a onClick={() => openDetail(detail.staff_id, detail.total_pages)}>Last</a>
          </div>
          <button className="btn btn-gray" onClick={() => exportCsv(detail.rows, ["No", "Tanggal", "User", "Action", "Dari Bank", "Tujuan Bank", "Jumlah", "Status", "Operator"], ["no", "tanggal", "user", "action", "dari_bank", "tujuan_bank", "jumlah", "status", "operator"], "laporan_detail.csv")} data-testid="laporan-detail-export">Export All Data</button>
        </div>
        <div className="history-title">Laporan ALL GAMES ({detail.date_from} / {detail.date_to}) &amp; USER : {detail.operator}</div>
        <table className="history-table" data-testid="laporan-detail-table">
          <thead>
            <tr>
              <th style={{ width: 34 }}>No</th><th>Tanggal Terima</th><th>User</th><th>Action</th>
              <th>Dari Bank</th><th>Tujuan Bank</th><th className="text-right">Jumlah</th><th>Status</th><th>Operator</th>
            </tr>
          </thead>
          <tbody>
            {detail.rows.length === 0 && <tr className="empty-row"><td colSpan={9}>Belum ada transaksi diproses</td></tr>}
            {detail.rows.map((r) => (
              <tr key={r.no} data-testid={`laporan-detail-row-${r.no}`}>
                <td>{r.no}</td>
                <td className="nowrap">{r.tanggal ? new Date(r.tanggal).toLocaleString("sv-SE").replace("T", " ") : ""}</td>
                <td>{r.user}</td>
                <td><span className="link-blue">{r.action}</span></td>
                <td>{r.dari_bank}</td>
                <td>{r.tujuan_bank}</td>
                <td className="text-right">{famt(r.jumlah)}</td>
                <td style={{ color: r.status === "ACCEPT" ? "#2e7d32" : "#c62828", fontWeight: "bold" }}>{r.status}</td>
                <td>{r.operator}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }

  // ---- Summary view ----
  if (!data) return <div className="muted" style={{ padding: 6 }}>Memuat laporan...</div>;
  return (
    <div data-testid="laporan-summary">
      <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 6 }}>
        <button className="btn btn-gray" onClick={() => exportCsv(data.rows, ["No", "Operator", "Deposit", "Withdraw", "Reject"], ["no", "operator", "deposit", "withdraw", "reject"], "laporan_all_games.csv")} data-testid="laporan-export">Export All Data</button>
      </div>
      <div className="history-title">Laporan ALL GAMES ({data.date_from} / {data.date_to})</div>
      <table className="history-table" data-testid="laporan-table">
        <thead>
          <tr>
            <th style={{ width: 40 }}>No</th><th>OPERATOR</th>
            <th className="text-right">DEPOSIT</th><th className="text-right">WITHDRAW</th><th className="text-right">REJECT</th>
          </tr>
        </thead>
        <tbody>
          {data.rows.length === 0 && <tr className="empty-row"><td colSpan={5}>Belum ada aktivitas staff</td></tr>}
          {data.rows.map((r) => (
            <tr key={r.staff_id} data-testid={`laporan-row-${r.operator}`}>
              <td>{r.no}</td>
              <td><a className="link-blue" onClick={() => openDetail(r.staff_id)} data-testid={`laporan-op-${r.operator}`}>{r.operator}</a></td>
              <td className="text-right">{r.deposit ? <a className="link-blue" onClick={() => openDetail(r.staff_id)}>{famt(r.deposit)}</a> : ""}</td>
              <td className="text-right">{r.withdraw ? <a className="link-blue" onClick={() => openDetail(r.staff_id)}>{famt(r.withdraw)}</a> : ""}</td>
              <td className="text-right">{r.reject ? <a className="link-blue" onClick={() => openDetail(r.staff_id)}>{famt(r.reject)}</a> : ""}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function TransaksiBaru() {
  const [tab, setTab] = useState("Deposit");
  const tabs = ["Deposit", "Withdraw", "Transaksi Manual", "Report"];
  return (
    <div>
      <div className="tabbar">
        {tabs.map((t) => (
          <div key={t} className={"tab" + (tab === t ? " active" : "")} onClick={() => setTab(t)} data-testid={`tab-${t.replace(/\s/g, "-").toLowerCase()}`}>{t}</div>
        ))}
      </div>
      {tab === "Deposit" && <PendingTable type="Deposit" />}
      {tab === "Withdraw" && <PendingTable type="Withdraw" />}
      {tab === "Transaksi Manual" && <ManualForm />}
      {tab === "Report" && <ReportView />}
    </div>
  );
}
